/**
 * Utilities for internal app URL handling with support for additional paths
 */

/**
 * Extract unique app URLs from content (handles markdown and plain text)
 * @param {string} content - The content to search
 * @returns {Array<{url: string, appId: string, path: string}>} Array of unique app URL objects
 */
export const extractAppUrls = (content) => {
  if (!content) return [];
  
  const appUrls = new Map(); // Use Map to maintain uniqueness by URL
  
  // Regex to match app URLs in various formats:
  // - Plain text: cellcog.ai/app/123 or cellcog.ai/app/123/file.html
  // - Markdown: [text](cellcog.ai/app/123) or [text](cellcog.ai/app/123/file.html)
  // - With protocol: https://cellcog.ai/app/123
  // Captures the full path after /app/ until a space, closing paren, or common punctuation.
  // The path character class also excludes markdown bold/italic markers (*), inline-code
  // backticks, quotes, angle brackets and closing braces. Agents very often wrap app URLs
  // in `backticks`; the closing backtick used to be swallowed into the captured path and
  // ended up percent-encoded as %60 in the app tile link (and in the tile label).
  const appUrlRegex = /(?:https?:\/\/)?cellcog\.ai\/app\/([a-zA-Z0-9-_]+(?:\/[^\s\)\],;*`'"<>}]*)?)/g;
  
  let match;
  while ((match = appUrlRegex.exec(content)) !== null) {
    // CEL-3946 (Nov 4 2026): link TEXT like `cellcog.ai/app/<ws>/pricing\_findings/` carries a markdown escape. The
    // backslash rode into the tile URL and browsers read it as "/", opening /pricing/_findings/ (404). Unescape it.
    let fullMatch = match[0].replace(/\\([\\`*_{}[\]()#+\-.!~|])/g, '$1');
    let pathAfterApp = match[1].replace(/\\([\\`*_{}[\]()#+\-.!~|])/g, '$1');
    
    // Clean up trailing punctuation, markdown markers, backticks and quotes
    // that are likely not part of the URL
    fullMatch = fullMatch.replace(/[\.\,\;\:\!\?\*`'"]+$/, '');
    pathAfterApp = pathAfterApp.replace(/[\.\,\;\:\!\?\*`'"]+$/, '');
    
    // Extract just the app ID (first segment)
    const appId = pathAfterApp.split('/')[0];
    
    // Ensure the URL has protocol
    const url = fullMatch.startsWith('http') 
      ? fullMatch 
      : `https://${fullMatch}`;
    
    // Store with full URL as key to prevent duplicates
    appUrls.set(url, { url, appId, path: pathAfterApp });
  }
  
  // Convert Map values to array
  return Array.from(appUrls.values());
};

/**
 * Human-readable label for an app link, shared by the chat app tile (and previously the View App
 * button). If the URL has a custom path with a filename ({id}/page.html), use that filename; else
 * "App" (or "App #n" when a message links multiple distinct apps).
 * @param {{url:string, appId:string, path:string}} appData
 * @param {number} index - position in the list (0-based)
 * @param {number} total - total app links in the message
 * @returns {string}
 */
export const appLabel = (appData, index = 0, total = 1) => {
  const pathParts = appData && appData.path
    ? appData.path.split('/').filter((part) => part.length > 0)
    : [];
  const hasCustomPath = pathParts.length > 1;
  if (hasCustomPath) {
    return pathParts[pathParts.length - 1];
  }
  return `App${total > 1 ? ` #${index + 1}` : ''}`;
};

/**
 * Get the app URL with proper protocol
 * @param {string} urlOrPath - The URL or path
 * @returns {string} The full URL with https protocol
 */
export const normalizeAppUrl = (urlOrPath) => {
  if (urlOrPath.startsWith('http')) {
    return urlOrPath;
  }
  if (urlOrPath.startsWith('cellcog.ai')) {
    return `https://${urlOrPath}`;
  }
  // Assume it's just the path part
  return `https://cellcog.ai/app/${urlOrPath}`;
};
